import { QueryKey, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../constants/queryKeys";
import { downloadReviewStats } from "../store/actions/stats";
import { useAppDispatch } from "../store/hooks";

interface ReviewQueryParams {
  engineerId?: number;
  artistId?: number;
  studioId?: number;
  studioRoomId?: number;
}

interface useGetReviews extends ReviewQueryParams {
  orderBy?: string;
  page?: number;
}

enum ReviewableEntities {
  ENGINEER = "engineer",
  ARTIST = "artist",
  STUDIO = "studio",
  STUDIO_ROOM = "studioRoom",
}

const getReviewQueryKey = (params: ReviewQueryParams): QueryKey => {
  const { engineerId, artistId, studioId, studioRoomId } = params;

  if (engineerId) {
    return [QUERY_KEYS.GET_REVIEWS, ReviewableEntities.ENGINEER, engineerId];
  }

  if (artistId) {
    return [QUERY_KEYS.GET_REVIEWS, ReviewableEntities.ARTIST, artistId];
  }

  if (studioId) {
    return [QUERY_KEYS.GET_REVIEWS, ReviewableEntities.STUDIO, studioId];
  }

  if (studioRoomId) {
    return [
      QUERY_KEYS.GET_REVIEWS,
      ReviewableEntities.STUDIO_ROOM,
      studioRoomId,
    ];
  }

  return [QUERY_KEYS.GET_REVIEWS];
};

export const useGetReviews = (args: useGetReviews) => {
  const dispatch = useAppDispatch();

  const queryKey = getReviewQueryKey(args);
  const { engineerId, artistId, studioId, studioRoomId, orderBy, page } = args;

  return useQuery({
    queryKey,
    enabled: Boolean(engineerId || artistId || studioId || studioRoomId),
    queryFn: async () => {
      return dispatch(
        downloadReviewStats({
          artist_id: artistId,
          engineer_id: engineerId,
          studio_id: studioId,
          studio_room_id: studioRoomId,
          order_by: orderBy,
          page: page ?? 1,
        }),
      ).unwrap();
    },
  });
};
